import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Stethoscope, Plus, Check, X as XIcon, UserX, CalendarClock, Trash2, StickyNote } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  useListConsultationsQuery,
  useGetConsultationQuery,
  useUpdateConsultationStatusMutation,
  useUpdateConsultationStageMutation,
  useAddConsultationActionNoteMutation,
  useDeleteConsultationActionNoteMutation,
} from '../../api/consultationsApi';
import { useUnscheduledConsultationsQuery } from '../../api/leadsApi';
import { CONSULTATION_STATUS, CONSULTATION_STAGE } from '../../utils/status';
import { apiErrorMessage } from '../../utils/apiError';
import { doctorDisplayName } from '../appointments/doctorColors';
import ScheduleConsultationModal from './ScheduleConsultationModal';
import AddPatientToConsultationModal from './AddPatientToConsultationModal';

const fmtDateTime = (d) =>
  new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

export default function ConsultationsPage() {
  const [filter, setFilter] = useState({ page: 1, pageSize: 25, status: '' });
  // Active = everything still in the funnel (Scheduled/OnlyConsultation/NoShow/Cancelled).
  // Converted patients are pulled out of the default view into their own tab — once a patient
  // books a real follow-up appointment or is billed for anything beyond a Doctor Consultation,
  // they're auto-moved here (see ConsultationConversionGuard on the backend).
  const [stageTab, setStageTab] = useState('active');
  const [showSchedule, setShowSchedule] = useState(false);
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [scheduleTarget, setScheduleTarget] = useState(null); // { leadId, subjectName }

  // Deep link of the form /consultations?openId=<guid> — e.g. clicking a consultation from global
  // search. Fetched directly by id and shown in its own modal, since the list here has no
  // per-row detail view and the target row may not be on the currently filtered/paged view.
  const [searchParams, setSearchParams] = useSearchParams();
  const [openConsultationId, setOpenConsultationId] = useState(null);
  useEffect(() => {
    const id = searchParams.get('openId');
    if (id) setOpenConsultationId(id);
  }, [searchParams]);
  const closeConsultationModal = () => {
    setOpenConsultationId(null);
    if (searchParams.get('openId')) {
      const next = new URLSearchParams(searchParams);
      next.delete('openId');
      setSearchParams(next, { replace: true });
    }
  };

  const { data, isFetching } = useListConsultationsQuery({
    ...filter,
    ...(stageTab === 'converted' ? { stage: 'Converted' } : { excludeConverted: true }),
  });
  // "Awaiting Scheduling" comes straight from the server: leads in ConsultationScheduled with
  // NO consultation and NO appointment row in the database. The old approach subtracted the
  // lead-ids visible on the CURRENT consultations page/tab client-side, so switching tab or
  // page made every scheduled lead "flood" into this card — the ids it subtracted changed
  // with whatever happened to be on screen.
  const { data: pendingLeads = [] } = useUnscheduledConsultationsQuery();
  const [updateStatus] = useUpdateConsultationStatusMutation();
  const [updateStage] = useUpdateConsultationStageMutation();

  const setStatus = async (id, status) => {
    try {
      await updateStatus({ id, status }).unwrap();
      toast.success(`Consultation → ${CONSULTATION_STATUS[status]?.label ?? status}`);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to update consultation'));
    }
  };

  const setStage = async (id, stage) => {
    try {
      await updateStage({ id, stage }).unwrap();
      toast.success(`Status → ${CONSULTATION_STAGE[stage]?.label ?? stage}`);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to update status'));
    }
  };

  const rows = data?.items ?? [];

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb"><span>Home</span><span className="bc-sep">/</span><span className="bc-cur">Consultations</span></div>
        <div className="page-header-row">
          <div>
            <h1>Consultations</h1>
            <div className="page-header-sub">Initial assessments and follow-up consultations.</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
            <button className="btn btn-primary" onClick={() => setShowSchedule(true)}>
              <Plus size={16} /> Schedule Consultation
            </button>
            <button className="btn btn-outline" onClick={() => setShowAddPatient(true)}>
              <Plus size={16} /> Add Patient
            </button>
          </div>
        </div>
      </div>

      {/* Pending scheduling — leads moved to consultation but not yet scheduled */}
      {pendingLeads.length > 0 && (
        <div className="card card-body" style={{ marginBottom: 20, borderLeft: '4px solid var(--amber-400)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <CalendarClock size={16} style={{ color: 'var(--amber-600)' }} />
            <h3 style={{ margin: 0, fontSize: '0.9rem', color: 'var(--amber-700)' }}>
              Awaiting Scheduling ({pendingLeads.length})
            </h3>
            <span className="td-muted" style={{ fontSize: '0.78rem' }}>
              These leads are marked for consultation but haven't been scheduled yet.
            </span>
          </div>
          <div className="tbl-wrap" style={{ marginTop: 0 }}>
            <table>
              <thead>
                <tr>
                  <th>Lead</th><th>Phone</th><th>Concern</th><th style={{ textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {pendingLeads.map((l) => (
                  <tr key={l.id}>
                    <td className="td-bold">{l.fullName}</td>
                    <td className="td-mono">{l.phoneNumber}</td>
                    <td className="td-muted" style={{ maxWidth: 280 }}>
                      <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {l.notes || l.concerns || '—'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => setScheduleTarget({ leadId: l.id, subjectName: l.fullName })}
                      >
                        <CalendarClock size={13} /> Schedule
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="tabs">
        <button
          className={`tab-btn ${stageTab === 'active' ? 'active' : ''}`}
          onClick={() => { setStageTab('active'); setFilter((f) => ({ ...f, page: 1 })); }}
        >
          Active
        </button>
        <button
          className={`tab-btn ${stageTab === 'converted' ? 'active' : ''}`}
          onClick={() => { setStageTab('converted'); setFilter((f) => ({ ...f, page: 1 })); }}
        >
          Converted
        </button>
      </div>

      <div className="toolbar">
        <select className="form-control" style={{ width: 220 }} value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value, page: 1 })}>
          <option value="">All statuses</option>
          {Object.entries(CONSULTATION_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th>When</th><th>Patient / Lead</th><th>Doctor</th><th>Type</th><th>Status</th>
              <th style={{ minWidth: 260 }}>Planned actions</th>
              <th style={{ textAlign: 'right' }}></th>
            </tr>
          </thead>
          <tbody>
            {isFetching && !rows.length && (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24 }}>
                <div className="skeleton" style={{ height: 20, maxWidth: 280, margin: '0 auto' }} />
              </td></tr>
            )}
            {rows.map((c) => {
              const who = c.patientName ?? c.leadName ?? '—';
              const tag = c.patientCode ? `Patient · ${c.patientCode}` : (c.leadId ? 'Lead' : '');
              const open = c.status === 'Scheduled' || c.status === 'Confirmed';
              return (
                <tr key={c.id}>
                  <td className="td-muted">{fmtDateTime(c.scheduledAt)}</td>
                  <td className="td-bold">{who}<div className="td-muted">{tag}</div></td>
                  <td>{c.doctorName ? doctorDisplayName(c.doctorName) : '—'}</td>
                  <td className="td-muted">{c.type}</td>
                  <td>
                    <select
                      className="form-control form-control-sm"
                      style={{ width: 160 }}
                      value={c.stage}
                      onChange={(e) => setStage(c.id, e.target.value)}
                    >
                      {Object.entries(CONSULTATION_STAGE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </td>
                  {/* Planned actions — multiple free-text note entries per row. */}
                  <td><ActionNotesCell consultation={c} /></td>
                  {/* Compact scheduling controls (kept so consultations can still be completed/cancelled). */}
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {open ? (
                      <>
                        <button className="btn btn-sm btn-success btn-icon" onClick={() => setStatus(c.id, 'Completed')} title="Mark completed">
                          <Check size={14} />
                        </button>{' '}
                        <button className="btn btn-sm btn-outline btn-icon" onClick={() => setStatus(c.id, 'NoShow')} title="No show">
                          <UserX size={14} />
                        </button>{' '}
                        <button className="btn btn-sm btn-outline btn-icon" onClick={() => setStatus(c.id, 'Cancelled')} title="Cancel">
                          <XIcon size={14} />
                        </button>
                      </>
                    ) : <span className="td-muted">—</span>}
                  </td>
                </tr>
              );
            })}
            {!isFetching && rows.length === 0 && (
              <tr><td colSpan={7}>
                <div className="empty-state">
                  <Stethoscope className="es-icon" size={42} />
                  <h3>{stageTab === 'converted' ? 'No converted patients yet' : 'No consultations'}</h3>
                  <p>
                    {stageTab === 'converted'
                      ? 'Patients land here automatically once they book a follow-up appointment or are billed beyond a Doctor Consultation.'
                      : 'Schedule a consultation for a lead or patient to get started.'}
                  </p>
                </div>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data && data.totalPages > 1 && (
        <div className="toolbar" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
          <button className="btn btn-outline btn-sm" disabled={!data.hasPrevious} onClick={() => setFilter({ ...filter, page: filter.page - 1 })}>Prev</button>
          <span className="td-muted">Page {data.page} / {data.totalPages}</span>
          <button className="btn btn-outline btn-sm" disabled={!data.hasNext} onClick={() => setFilter({ ...filter, page: filter.page + 1 })}>Next</button>
        </div>
      )}

      {showSchedule && <ScheduleConsultationModal onClose={() => setShowSchedule(false)} />}
      {showAddPatient && (
        <AddPatientToConsultationModal
          onClose={() => setShowAddPatient(false)}
          onCreated={() => setShowAddPatient(false)}
        />
      )}
      {scheduleTarget && (
        <ScheduleConsultationModal
          leadId={scheduleTarget.leadId}
          subjectName={scheduleTarget.subjectName}
          onClose={() => setScheduleTarget(null)}
          onCreated={() => setScheduleTarget(null)}
        />
      )}
      {openConsultationId && (
        <ConsultationDetailModal consultationId={openConsultationId} onClose={closeConsultationModal} />
      )}
    </>
  );
}

/**
 * Planned-action notes cell: a stack of free-text entries (each deletable) plus an inline
 * "add note" box. Notes live on the consultation row in the central DB — several per row.
 */
function ActionNotesCell({ consultation }) {
  const [text, setText] = useState('');
  const [addNote, { isLoading: adding }] = useAddConsultationActionNoteMutation();
  const [deleteNote] = useDeleteConsultationActionNoteMutation();
  const notes = consultation.actionNotes ?? [];

  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    try {
      await addNote({ id: consultation.id, text: t }).unwrap();
      setText('');
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not add the note'));
    }
  };

  return (
    <div style={{ display: 'grid', gap: 4 }}>
      {notes.map((n) => (
        <div key={n.id} style={{
          display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: '0.8rem',
          background: 'var(--bg-subtle, rgba(0,0,0,0.04))', borderRadius: 6, padding: '3px 8px',
        }}>
          <StickyNote size={12} style={{ flexShrink: 0, marginTop: 3, color: 'var(--amber-600, #d97706)' }} />
          <span style={{ flex: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{n.text}</span>
          <button
            type="button" className="btn-icon-sm" title="Remove note"
            style={{ flexShrink: 0 }}
            onClick={async () => {
              try { await deleteNote({ id: consultation.id, noteId: n.id }).unwrap(); }
              catch (e) { toast.error(apiErrorMessage(e, 'Could not remove the note')); }
            }}
          >
            <Trash2 size={12} color="var(--red-500)" />
          </button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 4 }}>
        <input
          className="form-control form-control-sm"
          style={{ fontSize: '0.78rem', height: 26 }}
          placeholder={notes.length ? 'Add another action…' : 'Planned action (e.g. call after X-ray)…'}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
        />
        <button type="button" className="btn btn-sm btn-outline" onClick={submit} disabled={adding || !text.trim()} title="Add note">
          <Plus size={13} />
        </button>
      </div>
    </div>
  );
}

/** Read-only summary shown for a deep-linked consultation — this page has no other per-row detail view. */
function ConsultationDetailModal({ consultationId, onClose }) {
  const { data: c, isFetching, error } = useGetConsultationQuery(consultationId);
  const who = c?.patientName ?? c?.leadName ?? '—';
  const tag = c?.patientCode ? `Patient · ${c.patientCode}` : (c?.leadId ? 'Lead' : '');
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Consultation</h2>
          <button className="modal-close" onClick={onClose}><XIcon size={18} /></button>
        </div>
        <div className="modal-body">
          {isFetching && !c && <div className="skeleton" style={{ height: 20, maxWidth: 280 }} />}
          {error && <div className="td-muted">Couldn't load this consultation.</div>}
          {c && (
            <div style={{ display: 'grid', gap: 10 }}>
              <div className="info-row"><div className="ir-label">Patient / Lead</div><div className="ir-value">{who}{tag && <span className="td-muted"> · {tag}</span>}</div></div>
              <div className="info-row"><div className="ir-label">Scheduled</div><div className="ir-value">{fmtDateTime(c.scheduledAt)}</div></div>
              <div className="info-row"><div className="ir-label">Doctor</div><div className="ir-value">{c.doctorName ? doctorDisplayName(c.doctorName) : '—'}</div></div>
              <div className="info-row"><div className="ir-label">Type</div><div className="ir-value">{c.type}</div></div>
              <div className="info-row"><div className="ir-label">Status</div><div className="ir-value">{CONSULTATION_STATUS[c.status]?.label ?? c.status}</div></div>
              <div className="info-row"><div className="ir-label">Stage</div><div className="ir-value">{CONSULTATION_STAGE[c.stage]?.label ?? c.stage}</div></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
